import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useChatContext } from "@/context/ChatContext";
import { cn } from "@/lib/utils";
import { MessageSquare, Archive } from "lucide-react";
import { useState } from "react";
import type { ChatConversation } from "@/types/chat";
import { useToast } from "@/hooks/useToast";
import { ArchivedConversationsList } from "@/components/chat/ArchivedConversationsList";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

// @ts-ignore - Honeybadger is a global
declare const Honeybadger: any;


export function ConversationsList() {
  const { loadConversation, getCurrentConversation, chats, isLoading, setChats } = useChatContext();
  const currentConversation = getCurrentConversation("research_assistant");
  const { toast } = useToast();
  const [search, setSearch] = useState("");

  if (isLoading) {
    return (
      <div className="space-y-2 px-2 py-2">
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-[200px]" />
        ))}
      </div>
    );
  }

  return (
    <div className="w-full max-w-[236px]">
      <div className="py-2 pl-3 pr-[10px]">
        <input
          type="text"
          placeholder="Search conversations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border border-input bg-input px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:ring-1 focus:ring-ring transition-colors"
        />
      </div>
      <div className="py-2" role="list" aria-label="Saved conversations list">
        {chats
          .filter((chat: ChatConversation) => chat.discarded_at === null)
          .filter((chat: ChatConversation) => (chat.kind ?? "research_assistant") === "research_assistant")
          .filter((chat: ChatConversation) => {
            const searchLower = search.trim().toLowerCase();
            return (
              chat.title?.toLowerCase().includes(searchLower) ||
              format(new Date(chat.created_at), "PPp").toLowerCase().includes(searchLower)
            );
          })
          .map((chat: ChatConversation) => {
            return (
              <div key={chat.id} className="flex w-full max-w-[236px] flex-row items-center">
                <Button
                  variant={currentConversation?.id === chat.id ? "secondary" : "ghost"}
                  className={cn(
                    "h-9 min-w-0 flex-1 justify-start px-3 py-0",
                    currentConversation?.id === chat.id && "border-2 border-primary font-medium",
                  )}
                  onClick={() => {
                    if (currentConversation?.id === chat.id) {
                      return;
                    }
                    loadConversation(chat.id, "research_assistant");
                  }}
                  role="listitem"
                >
                  <MessageSquare
                    className={cn("h-4 w-4 shrink-0", currentConversation?.id === chat.id && "text-primary")}
                  />
                  <span className="ml-2 overflow-hidden text-ellipsis max-w-[180px]" suppressHydrationWarning>
                    {chat.title || format(new Date(chat.created_at), "PPp")}
                  </span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => {
                    if (!window.confirm("Are you sure you want to archive this conversation?")) {
                      return;
                    }

                    try {
                      await fetch("/api/v1/chats/archive", {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          "X-CSRF-Token":
                            document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
                        },
                        body: JSON.stringify({ id: chat.id }),
                      });
                      setChats((prevChats) =>
                        prevChats.map((c) =>
                          c.id === chat.id ? { ...c, discarded_at: new Date().toISOString() } : c
                        )
                      );

                      toast({
                        title: "Chat archived",
                        description: "The conversation has been archived successfully.",
                      });
                    } catch (error) {
                      Honeybadger.notify(error);
                      toast({
                        title: "Error",
                        description: "Failed to archive the conversation.",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Archive className="h-4 w-4" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Archive chat</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Button>
              </div>
            );
          })}
      </div>
      <ArchivedConversationsList />
    </div>
  );
}
